import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Typography, message
} from 'antd';
import React, { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Input from 'common/components/Input';
import {
  getSystemGeneralService,
  updateSystemGeneralService,
} from 'common/services/systems';
import { UpdateSystemGeneralParams } from 'common/services/systems/types';
import { systemSchema } from 'common/utils/schemas';

type SystemGeneralForm = {
  gaIds: string;
  gtmIds: string;
  gMapId: string;
  salaryWarning?: string;
  salaryWarningYear?: string;
  workingDateWarning?: string;
  fileFavicon?: File;
  acApproveHour?: string;
  opApproveHour?: string;
  ktApproveHour?: string;
  ktEmail?: string;
};

const GeneralSM: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  /* Hooks */
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /* States */
  // const [fileList, setFileList] = useState<Array<UploadFile>>([]);

  /* React-hooks-form */
  const method = useForm<SystemGeneralForm>({
    mode: 'onSubmit',
    resolver: yupResolver(systemSchema),
    defaultValues: {
      gaIds: '',
      gtmIds: '',
      gMapId: '',
      fileFavicon: undefined,
    },
  });

  /* Queries */
  const { data: generalData } = useQuery(
    ['systemsManagement-general', 'list'],
    () => getSystemGeneralService()
  );

  const { mutate: editGeneralMutate, isLoading: isEditLoading } = useMutation(
    ['systemsManagement-general', 'edit'],
    async (params: UpdateSystemGeneralParams) => updateSystemGeneralService(params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        queryClient.invalidateQueries(['systemsManagement-general', 'list']);
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
    }
  );

  /* Functions */
  const handleSubmit = (data: SystemGeneralForm) => {
    editGeneralMutate({
      ...data,
    });
  };

  /* Effects */
  useEffect(() => {
    if (generalData) {
      method.reset({
        gaIds: generalData.gaIds?.toString(),
        gtmIds: generalData.gtmIds?.toString(),
        gMapId: generalData.gMapId,
        salaryWarning: generalData.salaryWarning,
        salaryWarningYear: generalData.salaryWarningYear,
        workingDateWarning: generalData.workingDateWarning,
        acApproveHour: generalData.acApproveHour,
        opApproveHour: generalData.opApproveHour,
        ktApproveHour: generalData.ktApproveHour,
        ktEmail: generalData.ktEmail,
      });
      // setFileList([
      //   {
      //     uid: '-1',
      //     name: generalData?.favicon?.split('/').slice(-1)[0] || 'favicon.ico',
      //     url: getImageURL(generalData?.favicon),
      //     thumbUrl: getImageURL(generalData?.favicon),
      //   },
      // ]);
    }
  }, [generalData, method]);

  return (
    <div className="p-system_general">
      <FormProvider<SystemGeneralForm> {...method}>
        <form noValidate>
          <Card type="inner">
            {/* <Space
              className="p-system_general_space"
              direction="vertical"
              size={16}
            >
              <Controller
                name="gaIds"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <div>
                    <Typography.Text strong>
                      {t('systemManagement.gaId')}
                      {' '}
                    </Typography.Text>
                    <Input
                      ref={ref}
                      onChange={onChange}
                      value={value}
                      error={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                name="gtmIds"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <>
                    <Typography.Text strong>
                      {t('systemManagement.gtmId')}
                    </Typography.Text>
                    <Input
                      ref={ref}
                      onChange={onChange}
                      value={value}
                      error={error?.message}
                    />
                  </>
                )}
              />

              <Controller
                name="gMapId"
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <>
                    <Typography.Text strong>
                      {t('systemManagement.gMapId')}
                    </Typography.Text>
                    <Input
                      ref={ref}
                      onChange={onChange}
                      value={value}
                      error={error?.message}
                    />
                  </>
                )}
              />

              <Controller
                name="fileFavicon"
                render={({ field: { onChange } }) => (
                  <Upload
                    listType="picture"
                    beforeUpload={() => false}
                    onChange={(info) => {
                      onChange(info.fileList?.[0]?.originFileObj);
                      setFileList([...info.fileList]);
                    }}
                    onRemove={() => onChange(undefined)}
                    maxCount={1}
                    fileList={fileList}
                    accept="image/x-icon"
                  >
                    <Button type="default">
                      {t('systemManagement.uploadFavicon')}
                    </Button>
                  </Upload>
                )}
              />
            </Space> */}

            <Controller
              name="salaryWarning"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.salaryWarning')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                    hasComma
                  />
                </div>
              )}
            />

            <Controller
              name="salaryWarningYear"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.salaryWarningYear')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                    hasComma
                  />
                </div>
              )}
            />

            <Controller
              name="workingDateWarning"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.workingDateWarning')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                    hasComma
                  />
                </div>
              )}
            />

            <Controller
              name="opApproveHour"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.opApproveHour')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                  />
                </div>
              )}
            />

            <Controller
              name="acApproveHour"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.acApproveHour')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                  />
                </div>
              )}
            />

            <Controller
              name="ktApproveHour"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.ktApproveHour')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                    type="number"
                  />
                </div>
              )}
            />

            <Controller
              name="ktEmail"
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => (
                <div className="u-mt-8">
                  <Typography.Text strong>
                    {t('system.ktEmail')}
                  </Typography.Text>
                  <Input
                    ref={ref}
                    onChange={onChange}
                    value={value}
                    error={error?.message}
                  />
                </div>
              )}
            />
            <div className="p-system_general_submit u-mt-16">
              <Button
                type="primary"
                disabled={!canEdit}
                loading={isEditLoading}
                onClick={method.handleSubmit(handleSubmit)}
              >
                <SaveOutlined />
                {t('system.save')}
              </Button>
            </div>
          </Card>
        </form>
      </FormProvider>
    </div>
  );
};

export default GeneralSM;
